import { useState } from 'react'
import {
  Monitor,
  Apple,
  Terminal,
  Smartphone,
  Download as DownloadIcon,
  ArrowUpRight,
  Check,
  Package,
  Link2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import ProductPreview from '../components/ProductPreview'
import { useApi } from '../hooks/useApi'
import { downloadApi } from '../lib/api'
import { PageHeading, ServiceState } from '../components/Interior'
const platforms = [
  { key: 'windows', label: 'Windows', icon: Monitor },
  { key: 'macos', label: 'macOS', icon: Apple },
  { key: 'linux', label: 'Linux', icon: Terminal },
  { key: 'ios', label: 'iOS', icon: Smartphone },
  { key: 'android', label: 'Android', icon: Smartphone },
]
export default function Download() {
  const { t } = useTranslation(['download', 'common'])
  const [platform, setPlatform] = useState('windows')
  const {
    data: links,
    loading,
    error,
    refetch,
  } = useApi(() => downloadApi.getAll(), [])
  const selected = platforms.find((item) => item.key === platform)!
  const Icon = selected.icon
  const visible = (links || []).filter((link) => link.platform === platform)
  return (
    <div className="designed-page download-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/download"
        structuredData={[schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.download'), url: '/download' }]), schemas.softwareApp]}
      />
      <PageHeading
        label={t('heading.label')}
        title={t('heading.title')}
        accent={t('heading.accent')}
        description={t('heading.description')}
      >
        <div className="download-package" aria-hidden="true">
          <span>{t('package.label')}</span>
          <img src="/brand/mark.svg" width="170" height="125" alt="" />
          <strong>CMDesk</strong>
          <small>Remote Desktop</small>
          <div>
            <Package size={16} /> {t('package.note')}
          </div>
        </div>
      </PageHeading>
      <section className="download-workbench">
        <div className="platform-tabs" aria-label={t('platformsLabel')}>
          {platforms.map(({ key, label, icon: PlatformIcon }) => (
            <button
              key={key}
              aria-pressed={platform === key}
              onClick={() => setPlatform(key)}
            >
              <PlatformIcon size={20} />
              <span>{label}</span>
              <ArrowUpRight size={15} />
            </button>
          ))}
        </div>
        <div className="download-sheet">
          <div className="download-sheet-heading">
            <span className="platform-icon">
              <Icon size={32} />
            </span>
            <div>
              <span className="eyebrow">{t('sheet.eyebrow')}</span>
              <h2>{t('sheet.title', { platform: selected.label })}</h2>
            </div>
          </div>
          <ServiceState
            loading={loading}
            error={error}
            empty={!visible.length}
            retry={refetch}
          />
          {!loading &&
            !error &&
            visible.map((link) => (
              <article className="download-release" key={link.id}>
                <div>
                  <h3>{link.variant}</h3>
                  <p>
                    {[link.version && t('sheet.version', { version: link.version }), link.fileSize]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {link.requirements && <small>{link.requirements}</small>}
                </div>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="jade-button"
                  onClick={() => {
                    void downloadApi.track(link.id).catch(() => {})
                  }}
                >
                  <DownloadIcon size={17} /> {t('sheet.download')}
                  <span className="sr-only">{t('sheet.newTab')}</span>
                </a>
              </article>
            ))}
          <div className="download-sheet-note">
            <Check size={15} /> {t('sheet.note')}
          </div>
        </div>
      </section>
      <div className="download-help-row">
        <Link to="/docs">
          <Link2 size={20} />
          <span>
            {t('help.guideTitle')}<small>{t('help.guideText')}</small>
          </span>
          <ArrowUpRight size={19} />
        </Link>
        <Link to="/changelog">
          <Package size={20} />
          <span>
            {t('help.changelogTitle')}
            <small>{t('help.changelogText')}</small>
          </span>
          <ArrowUpRight size={19} />
        </Link>
      </div>
      <section className="download-product-gallery">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{t('gallery.eyebrow')}</span>
            <h2>
              {t('gallery.title')}
              <br />
              {t('gallery.titleLine2')}
            </h2>
          </div>
          <p>
            {t('gallery.text')}
            <br />
            {t('gallery.textLine2')}
          </p>
        </div>
        <ProductPreview id="download-product-preview" />
      </section>
    </div>
  )
}
